// « Ajouter une source » (rôle administration) : choix dans le catalogue ; un logiciel avec connecteur donne un
// formulaire (adresse, nom du compte attendu, RÉFÉRENCE du secret dans le coffre), un logiciel lu par export donne la
// recette d'export, sans formulaire de connexion.
// Aucun secret : pas de champ mot de passe, rien dans le stockage du navigateur, l'adresse ou la console. La saisie
// vit dans l'état React de ce seul écran.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type FormEvent } from 'react';
import { ErreurApi, nouvelleCle } from '../api/client';
import type { CatalogueSources, DeclarationSource, LogicielCatalogue, RecetteExport } from '../api/types';
import { Attendre, Erreur } from '../composants/communs';
import { useEkera } from '../contexte';
import { natureDuLogiciel } from '../domaine/depots';
import { frequenceValide, parFamille, problemeReference, type ChampsDeclaration } from '../domaine/sources';
import { lienVers, naviguer } from '../routeur';

export function AjouterSource() {
  const { client, administrateur } = useEkera();
  const catalogue = useQuery({ queryKey: ['catalogue-sources'], queryFn: () => client.catalogueSources(), staleTime: 300_000 });
  if (!administrateur)
    return (
      <section className="panneau" aria-labelledby="t-reserve">
        <h2 id="t-reserve">Réservé à l’administration</h2>
        <p>
          Déclarer une source demande le rôle « administration ». Vous pouvez consulter les sources déjà déclarées dans l’onglet
          « Sources lues ».
        </p>
      </section>
    );
  return (
    <Attendre etats={[catalogue]} quoi="du catalogue des sources">
      {() => (catalogue.data ? <Choix catalogue={catalogue.data} /> : null)}
    </Attendre>
  );
}

function Choix({ catalogue }: { catalogue: CatalogueSources }) {
  const [id, setId] = useState('');
  const idChoix = useId();
  const logiciel = catalogue.logiciels.find((l) => l.id === id);
  return (
    <>
      <section className="panneau" aria-labelledby="t-choix">
        <h2 id="t-choix">Quel logiciel voulez-vous faire lire ?</h2>
        <div className="champ">
          <label htmlFor={idChoix}>Logiciel du catalogue</label>
          <select id={idChoix} value={id} onChange={(e) => setId(e.target.value)}>
            <option value="">— Choisir un logiciel —</option>
            {parFamille(catalogue.logiciels).map((g) => (
              <optgroup key={g.famille} label={g.famille}>
                {g.logiciels.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.nom} ({l.mode === 'connecteur' ? 'connecteur' : 'export à déposer'})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <p className="note">
          Avec un <b>connecteur</b>, le collecteur sur site lit le logiciel chez vous. Sinon, vous exportez un fichier en suivant
          la recette d’Ekera.
        </p>
      </section>
      {logiciel?.mode === 'connecteur' && <FormulaireConnecteur key={logiciel.id} logiciel={logiciel} catalogue={catalogue} />}
      {logiciel?.mode === 'export' && (
        <section className="panneau" aria-labelledby="t-export">
          <h2 id="t-export">{logiciel.nom} : pas de connecteur, un export à déposer</h2>
          <div className="encadre info">
            <p>
              Il n’y a rien à connecter pour ce logiciel : <b>aucune adresse, aucun compte, aucun secret</b> n’est demandé. Suivez
              la recette ci-dessous et gardez le fichier produit.
            </p>
          </div>
          {logiciel.recette_export && <RecetteExportVue recette={logiciel.recette_export} />}
          <p>
            <a className="btn principal" href={lienVers('depot', { nature: natureDuLogiciel(logiciel.id) })}>
              Déposer l’export
            </a>{' '}
            <span className="note">
              Le fichier est déposé dans l’écran « Dépôt des documents » : son type y est reconnu au contenu, il est rangé chiffré,
              puis lu avec les autres pièces.
            </span>
          </p>
        </section>
      )}
    </>
  );
}

export function RecetteExportVue({ recette }: { recette: RecetteExport | null }) {
  if (!recette) return null;
  return (
    <div className="pile" data-testid="recette-export">
      <h3>{recette.titre}</h3>
      <ol className="liste-etapes">
        {recette.etapes.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ol>
      <p>
        <b>Formats acceptés :</b> {recette.formats.map((f) => f.toUpperCase()).join(', ')}.
      </p>
      <div className="encadre attention">
        <p>
          <b>À ne pas inclure dans l’export :</b> {recette.ne_pas_inclure.join(', ')}.
        </p>
      </div>
    </div>
  );
}

const VIDE: ChampsDeclaration = { nom: '', adresse: '', compte: '', reference: '', frequence: '' };

function FormulaireConnecteur({ logiciel, catalogue }: { logiciel: LogicielCatalogue; catalogue: CatalogueSources }) {
  const { client, cid } = useEkera();
  const qc = useQueryClient();
  const [c, setC] = useState<ChampsDeclaration>(VIDE);
  const [etape, setEtape] = useState<'saisie' | 'confirmation'>('saisie');
  const [problemes, setProblemes] = useState<Partial<Record<keyof ChampsDeclaration, string>>>({});
  const cle = useRef<string | null>(null);
  const id = useId();

  const m = useMutation({
    mutationFn: () => {
      cle.current ??= nouvelleCle();
      const d: DeclarationSource = {
        logiciel: logiciel.id,
        nom: c.nom.trim() || null,
        adresse: c.adresse.trim(),
        compte: c.compte.trim(),
        reference_secret: c.reference.trim(),
        frequence_lecture_h: c.frequence.trim() ? Number(c.frequence) : null,
      };
      return client.declarerSource(cid, d, cle.current);
    },
    onError: (e) => {
      // Un secret a été refusé : on n'en garde aucune trace dans l'écran (champs vidés, retour à la saisie).
      if (e instanceof ErreurApi && e.code === 'secret_refuse') {
        cle.current = null;
        setC(VIDE);
        setEtape('saisie');
      }
    },
    onSuccess: async (s) => {
      cle.current = null;
      setC(VIDE); // rien ne reste en mémoire une fois la déclaration faite
      await qc.invalidateQueries({ queryKey: ['carte', cid, 'sources'] });
      naviguer('sources', { source: s.id });
    },
  });

  const maj = (k: keyof ChampsDeclaration) => (v: string) => {
    cle.current = null; // un autre contenu : une autre clé d'idempotence
    setC((x) => ({ ...x, [k]: v }));
    setProblemes((p) => ({ ...p, [k]: undefined }));
  };

  function verifier(e: FormEvent) {
    e.preventDefault();
    const p: Partial<Record<keyof ChampsDeclaration, string>> = {};
    if (!c.adresse.trim()) p.adresse = 'Indiquez l’adresse du logiciel.';
    if (!c.compte.trim()) p.compte = 'Indiquez le nom du compte de lecture attendu.';
    const r = problemeReference(c.reference, catalogue.coffres);
    if (r) p.reference = r;
    if (!frequenceValide(c.frequence)) p.frequence = 'Un nombre entier d’heures, de 1 à 720.';
    setProblemes(p);
    if (Object.keys(p).length === 0) setEtape('confirmation');
  }

  const defaut = catalogue.seuils.frequence_lecture_h;
  return (
    <section className="panneau" aria-labelledby="t-form">
      <h2 id="t-form">Déclarer {logiciel.nom}</h2>
      <dl className="paires">
        <dt>Droits minimaux du compte de lecture</dt>
        <dd>{logiciel.lecture.droits_minimaux}</dd>
        <dt>Méthode de lecture</dt>
        <dd>{logiciel.lecture.methode}</dd>
      </dl>
      <div className="encadre attention" role="note">
        <p>
          <b>Ne saisissez jamais le mot de passe ici : seulement sa référence dans votre coffre.</b> Le secret reste dans votre
          coffre ; le collecteur sur site l’y lit. Ekera ne le voit, ne le reçoit et ne le garde jamais.
        </p>
      </div>
      {m.error && <Erreur erreur={m.error} titre="La source n’a pas été déclarée." />}
      {etape === 'saisie' ? (
        <form className="pile" onSubmit={verifier} noValidate autoComplete="off">
          <Champ id={`${id}-nom`} libelle="Nom affiché (facultatif)" aide={`Sinon : « ${logiciel.nom} »`} valeur={c.nom} onChange={maj('nom')} />
          <Champ id={`${id}-adresse`} libelle="Adresse du logiciel" aide="Hôte, ou « https://hôte[:port][/chemin] » ; sans identifiant ni paramètre" valeur={c.adresse} onChange={maj('adresse')} erreur={problemes.adresse} />
          <Champ id={`${id}-compte`} libelle="Nom du compte de lecture attendu" aide="Le nom du compte seulement, jamais son mot de passe" valeur={c.compte} onChange={maj('compte')} erreur={problemes.compte} />
          <Champ
            id={`${id}-ref`}
            libelle="Référence du secret dans votre coffre"
            aide="Une référence, pas un mot de passe"
            valeur={c.reference}
            onChange={maj('reference')}
            erreur={problemes.reference}
            mono
          />
          <div className="aide-coffres">
            <p className="note">Formats de référence acceptés :</p>
            <ul className="liste-points" aria-label="Formats de référence des coffres">
              {catalogue.coffres.map((k) => (
                <li key={k.schema} className="ok">
                  <b>{k.nom}</b> : <span className="num">{k.forme}</span>
                  <small>
                    Exemple : <span className="num">{k.exemple}</span>
                  </small>
                </li>
              ))}
            </ul>
          </div>
          <Champ
            id={`${id}-freq`}
            libelle="Fréquence de lecture en heures (facultatif)"
            aide={defaut ? `Sinon ${defaut} h` : undefined}
            valeur={c.frequence}
            onChange={maj('frequence')}
            erreur={problemes.frequence}
          />
          <div className="ligne">
            <button type="submit" className="btn principal">
              Vérifier la déclaration
            </button>
          </div>
        </form>
      ) : (
        <div className="pile">
          <div className="encadre info" role="region" aria-label="Simulation">
            <p>
              <b>Ce qui sera déclaré.</b> Rien n’est encore enregistré ; aucune connexion n’est faite par Ekera.
            </p>
            <dl className="paires">
              <dt>Logiciel</dt>
              <dd>{logiciel.nom}</dd>
              <dt>Nom affiché</dt>
              <dd>{c.nom.trim() || logiciel.nom}</dd>
              <dt>Adresse</dt>
              <dd>{c.adresse.trim()}</dd>
              <dt>Compte de lecture attendu</dt>
              <dd>{c.compte.trim()}</dd>
              <dt>Référence du secret</dt>
              <dd>
                <span className="num">{c.reference.trim()}</span>
              </dd>
              <dt>Fréquence de lecture</dt>
              <dd>{c.frequence.trim() ? `${c.frequence.trim()} h` : defaut ? `${defaut} h (par défaut)` : 'par défaut'}</dd>
            </dl>
          </div>
          <div className="ligne">
            <button type="button" className="btn principal" disabled={m.isPending} onClick={() => m.mutate()}>
              Confirmer la déclaration
            </button>
            <button
              type="button"
              className="btn"
              disabled={m.isPending}
              onClick={() => {
                m.reset();
                setEtape('saisie');
              }}
            >
              Modifier
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Champ({
  id,
  libelle,
  aide,
  valeur,
  onChange,
  erreur,
  mono,
}: {
  id: string;
  libelle: string;
  aide?: string;
  valeur: string;
  onChange: (v: string) => void;
  erreur?: string;
  mono?: boolean;
}) {
  return (
    <div className="champ">
      <label htmlFor={id}>
        {libelle}
        {aide && <span className="aide"> — {aide}</span>}
      </label>
      {/* type « text » exprès : jamais de champ de mot de passe ; pas d'enregistrement ni de correction par le navigateur */}
      <input
        id={id}
        type="text"
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-invalid={erreur ? true : undefined}
        aria-describedby={erreur ? `${id}-err` : undefined}
        style={mono ? { fontFamily: 'var(--mono)' } : undefined}
      />
      {erreur && (
        <p id={`${id}-err`} className="note" role="alert" style={{ color: 'var(--crit)' }}>
          {erreur}
        </p>
      )}
    </div>
  );
}
